import { datadogRum } from "@datadog/browser-rum";
import { zodResolver } from "apps/web/src/utils/zodResolver";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Menu, MenuItem, List, ListItem, Switch, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Loading } from "shared-ui/global/Loading";
import { useDateFormatting } from "shared-ui/contexts/Locale/useDateFormatting";
import { serializeDate } from "@notemeal/utils-date-time";
import { ConfirmationDialog } from "apps/web/src/componentLibrary";
import SomethingWentWrongDialog from "apps/web/src/componentLibrary/ConfirmationDialog/SomethingWentWrongDialog";
import { TWTabGroup } from "apps/web/src/componentLibrary/TWTabGroup/TWTabGroup";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import LoadingButton from "apps/web/src/components/universal/LoadingButton";
import { useBrowserBackAndRefreshWarning } from "apps/web/src/hooks/useBrowserBackAndRefreshWarning";
import { getNavOrgKitchenMenuScheduleImport } from "apps/web/src/pages/Auth/Org/Kitchen/KitchenPaths";
import { trackEvent } from "apps/web/src/reporting/reporting";
import {
  PlannedMenuMealInput,
  useLockPlannedMenuMutation,
  usePlannedMenuLockTakenSubscription,
  useSavePlannedMealsMutation,
} from "apps/web/src/types";
import { useUser } from "apps/web/src/utils/tokens";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom-v5-compat";
import { MenuBuilderSchema, MenuBuilderType } from "./Meal/MenuBuilderMealSchema";
import { MenuBuilderExportButton } from "./MenuBuilderExportButton";
import { MenuBuilderLockBanner } from "./MenuBuilderLockBanner";
import { useMenuBuilderContext } from "./MenuBuilderProvider";
import { MenuBuilderWeek } from "./MenuBuilderWeek";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";
import { MenuBuilderPanelToCellDnd } from "./MenuItemsPanel/MenuBuilderPanelToCellDnd";
import { MenuItemsSidePanel } from "./MenuItemsPanel/MenuItemsSidePanel";
import {
  MENU_ITEMS_PANEL_COLLAPSED_WIDTH,
  MENU_ITEMS_PANEL_EXPANDED_WIDTH,
} from "./MenuItemsPanel/constants";
import { useNutritionFlags } from "apps/web/src/hooks/useNutritionFlags";

export const MenuBuilderGrid = () => {
  const navigate = useNavigate();
  const currentUser = useUser();
  const { isMenuBuilderMultiWeekCopyingEnabled, isDragAndDropMenuItemsEnabled } = useNutritionFlags();
  const { formatYearlessDateWithLocale } = useDateFormatting();
  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const gridContentRef = useRef<HTMLDivElement>(null);
  const [panelTop, setPanelTop] = useState(0);
  // Collapsed by default on first load (NUT-2229)
  const [isMenuItemsPanelExpanded, setIsMenuItemsPanelExpanded] = useState(false);
  const menuItemsPanelWidth = isMenuItemsPanelExpanded
    ? MENU_ITEMS_PANEL_EXPANDED_WIDTH
    : MENU_ITEMS_PANEL_COLLAPSED_WIDTH;
  const {
    menuId,
    menuName,
    weeks,
    loading,
    selectedWeekIndex,
    setSelectedWeekIndex,
    mealsToDelete,
    setMealsToDelete,
    rowsToDelete,
    setRowsToDelete,
    rowItemsToDelete,
    setRowItemsToDelete,
    disabledDays,
    isEditable,
    updatedFormMeals,
    clearUpdatedMeals,
    currentUserHasLock,
    setCurrentUserHasLock,
    displayMacros,
    setDisplayMacros,
    markMealAsUpdated,
    markMealsAsUpdated,
    clearUpdatedMealsForWeek,
  } = useMenuBuilderContext();

  const [errorWhileSavingDialog, setErrorWhileSavingDialog] = useState(false);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogueOpen] = useState(false);
  const [weeksWithErrors, setWeeksWithErrors] = useState<number[]>([]);
  const { setMessage } = useSnackbar();
  const { setBrowserBackAndRefreshWarningEnabled } = useBrowserBackAndRefreshWarning();
  const [errorWhileLockingMenu, setErrorWhileLockingMenu] = useState(false);
  const [lockTakenDetails, setLockTakenDetails] = useState<{ lockCreatedByName: string; lockCreatedAgo: string } | null>(null);
  const [copyWeekConfirmation, setCopyWeekConfirmation] = useState<{ sourceWeekIndex: number; targetWeekIndex: number } | null>(null);

  const [lockPlannedMenu] = useLockPlannedMenuMutation({
    onError: e => {
      console.error(e);
      setErrorWhileLockingMenu(true);
    },
    onCompleted: () => {
      trackEvent("Nutrition | Kitchen | Menu Builder | User Got Edit Access", { menuId });
      setCurrentUserHasLock(true);
    },
  });

  const [savePlannedMeals, { loading: loadingSavePlannedMeals }] = useSavePlannedMealsMutation({
    refetchQueries: ["PlannedMenuGrid"],
    onError: e => {
      setErrorWhileSavingDialog(true);
      console.error(e);
    },
    onCompleted: () => {
      datadogRum.addAction("menu_builder.saved_menu", { menuId });
      setMessage("success", `${menuName} has been saved`);
    },
  });

  usePlannedMenuLockTakenSubscription({
    variables: { plannedMenuId: menuId },
    onSubscriptionData: async data => {
      const plannedMenu = data.subscriptionData.data?.plannedMenuLockTaken.payload.plannedMenu;
      const lockCreatedBy = plannedMenu?.lockCreatedBy;
      if (lockCreatedBy?.id !== currentUser?.id) {
        trackEvent("Nutrition | Kitchen | Menu Builder | User Lost Edit Access", { menuId });
        // undo any changes they had made before losing the lock
        form.reset();
        setLockTakenDetails({
          lockCreatedByName: lockCreatedBy ? `${lockCreatedBy.firstName} ${lockCreatedBy.lastName}` : "unknown",
          lockCreatedAgo: plannedMenu?.lockCreatedAt ? formatDistanceToNow(new Date(plannedMenu?.lockCreatedAt)) : "",
        });
      }
    },
  });

  const form = useForm<MenuBuilderType>({
    resolver: zodResolver(MenuBuilderSchema),
    defaultValues: { weeks: [] },
  });

  const isFormEdited = updatedFormMeals.length > 0 || mealsToDelete.length > 0 || rowsToDelete.length > 0 || rowItemsToDelete.length > 0;

  // lock the menu when the user makes their first edit
  useEffect(() => {
    if (isFormEdited && !currentUserHasLock) {
      lockPlannedMenu({ variables: { input: { plannedMenuId: menuId } } });
    }
  }, [isFormEdited, lockPlannedMenu, menuId, currentUserHasLock]);

  useEffect(() => {
    setBrowserBackAndRefreshWarningEnabled(isFormEdited);
  }, [isFormEdited, setBrowserBackAndRefreshWarningEnabled]);

  useLayoutEffect(() => {
    if (!isDragAndDropMenuItemsEnabled) {
      return;
    }

    const updatePanelTop = () => {
      setPanelTop(gridContentRef.current?.getBoundingClientRect().top ?? 0);
    };

    updatePanelTop();
    window.addEventListener("resize", updatePanelTop);

    const resizeObserver =
      typeof ResizeObserver !== "undefined" && gridContentRef.current
        ? new ResizeObserver(updatePanelTop)
        : null;
    if (gridContentRef.current && resizeObserver) {
      resizeObserver.observe(gridContentRef.current);
    }

    return () => {
      window.removeEventListener("resize", updatePanelTop);
      resizeObserver?.disconnect();
    };
  }, [isDragAndDropMenuItemsEnabled, loading, currentUserHasLock]);

  if (loading) {
    return (
      <Box sx={{ height: "100%" }}>
        <Loading progressSize="lg" />
      </Box>
    );
  }
  const weekLabels = weeks.map(
    (week, index) => `Week ${index + 1} (${formatYearlessDateWithLocale(week.startDate)} - ${formatYearlessDateWithLocale(week.endDate)})`
  );

  const trackEventReOrder = () => {
    const hasReOrderedRows = form.formState.dirtyFields.weeks?.some((week: any) =>
      week.meals?.some((meal: any) => meal?.rows?.some((row: any) => row.diningStationName === true))
    );

    if (hasReOrderedRows) {
      trackEvent("Nutrition | Web | Kitchen | Menu Builder | Re-order Dining Stations", { menuId });
    }
  };

  // manually handle validation and submission because we want to skip validation on empty meals
  const handleSave = async () => {
    trackEvent("menu_builder.clicked_save", { menuId });
    trackEventReOrder();
    const uniqueMealFormPaths = Array.from(updatedFormMeals);

    if (await form.trigger(uniqueMealFormPaths)) {
      const mealsToUpsert: PlannedMenuMealInput[] = uniqueMealFormPaths.map(formId => {
        const meal = form.getValues(formId);
        return {
          id: meal.id,
          name: meal.mealName,
          type: meal.mealType,
          startTime: meal.startTime,
          endTime: meal.endTime,
          plannedMenuWeekId: meal.weekId,
          themes: meal.themes,
          plannedMenuMealRows: meal.rows.map(row => ({
            id: row.id,
            position: row.position,
            diningStationName: row.diningStationName,
            foodType: row.foodType,
            plannedMenuMealRowItems: row.items.map(item => ({
              id: item.id,
              dayOfWeek: item.dayOfWeek,
              position: item.position,
              menuItemId: item.menuItem.id,
            })),
          })),
        };
      });
      if (mealsToUpsert.length > 0 || mealsToDelete.length > 0 || rowsToDelete.length > 0 || rowItemsToDelete.length > 0) {
        await savePlannedMeals({
          variables: {
            input: {
              plannedMenuId: menuId,
              mealsToDelete,
              rowsToDelete,
              rowItemsToDelete,
              mealsToUpsert,
              disabledDays,
            },
          },
        });
        // set form to not dirty (while keeping edits), but don't unmount everything
        form.reset(form.getValues(), { keepValues: true });
        setMealsToDelete([]);
        setRowsToDelete([]);
        setRowItemsToDelete([]);
        clearUpdatedMeals();
      }
    } else {
      // failed validation, check if there are errors in other week tabs
      const errors = form.formState.errors.weeks;
      if (Array.isArray(errors)) {
        const errorIndices = errors.reduce((indices, hasError, index) => (hasError ? [...indices, index] : null), []);
        datadogRum.addAction("menu_builder.save_failed_validation", { menuId, errors });
        if (errorIndices.length > 1 || errorIndices[0] !== selectedWeekIndex) {
          setWeeksWithErrors(errorIndices);
        }
      }
    }
  };

  const handlePublishClick = () => {
    datadogRum.addAction("menu_builder.table.clicked_publish_menu", { menuId });

    if (isFormEdited) {
      setUnsavedChangesDialogueOpen(true);
    } else {
      navigate(getNavOrgKitchenMenuScheduleImport(serializeDate(weeks[0].startDate), menuId));
    }
  };

  const checkWeekHasData = (weekIndex: number): boolean => {
    const targetWeekMeals = form.watch(`weeks.${weekIndex}.meals`);
    return targetWeekMeals && targetWeekMeals.length > 0 && targetWeekMeals.some(meal => meal.mealName !== "");
  };

  const handleCopyWeek = (sourceWeekIndex: number, targetWeekIndex: number) => {
    trackEvent("Nutrition | Kitchen | Menu Builder | User Copied Week", { menuId });
    const sourceWeekMeals = form.getValues(`weeks.${sourceWeekIndex}.meals`);
    const targetWeekId = weeks[targetWeekIndex].id;

    // Capture existing meals in target week and mark them for deletion
    const existingTargetMeals = form.getValues(`weeks.${targetWeekIndex}.meals`) || [];
    const existingMealIds = existingTargetMeals
      .map(meal => meal.id)
      .filter((id): id is string => id !== null && id !== undefined && id !== "");

    if (existingMealIds.length > 0) {
      setMealsToDelete([...mealsToDelete, ...existingMealIds]);
    }

    // Deep clone the source meals and update their weekId to match the target week
    // clearing the ids so it creates a new meals, rows, and items
    const copiedMeals = sourceWeekMeals.map(meal => ({
      ...meal,
      id: null,
      weekId: targetWeekId,
      rows: meal.rows.map(row => ({
        ...row,
        id: null,
        items: row.items.map(item => ({
          ...item,
          id: null,
        })),
      })),
    }));

    // Clear any stale tracked paths for the target week before replacing
    clearUpdatedMealsForWeek(targetWeekIndex);

    // Replace the target week's meals with the copied meals
    form.setValue(`weeks.${targetWeekIndex}.meals`, copiedMeals, { shouldDirty: true });

    // Mark all copied meals as updated (batch to avoid stale closure issues)
    const mealFormPaths = copiedMeals.map((_, mealIndex) => 
      `weeks.${targetWeekIndex}.meals.${mealIndex}` as `weeks.${number}.meals.${number}`
    );
    markMealsAsUpdated(mealFormPaths);

    setMessage("success", `Week ${sourceWeekIndex + 1} has been copied to Week ${targetWeekIndex + 1}`);
    datadogRum.addAction("menu_builder.copied_week", { menuId, sourceWeekIndex, targetWeekIndex });
  };

  const handleCopyWeekClick = (sourceWeekIndex: number) => {
    const targetWeekIndex = selectedWeekIndex;

    if (checkWeekHasData(targetWeekIndex)) {
      setCopyWeekConfirmation({ sourceWeekIndex, targetWeekIndex });
    } else {
      handleCopyWeek(sourceWeekIndex, targetWeekIndex);
    }
  };

  const handleConfirmCopyWeek = () => {
    if (copyWeekConfirmation) {
      handleCopyWeek(copyWeekConfirmation.sourceWeekIndex, copyWeekConfirmation.targetWeekIndex);
      setCopyWeekConfirmation(null);
    }
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        flex: 1,
        height: "100%",
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <MenuBuilderLockBanner />
      <Box sx={{ pb: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, minWidth: 0 }}>
        <TWTabGroup
          sx={{ maxWidth: 1000 }}
          tabs={weekLabels}
          onSelected={selected => setSelectedWeekIndex(weekLabels.indexOf(selected))} />
        {isEditable && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <FormControlLabel
              sx={{ mr: 0.5 }}
              control={<Switch
                sx={{ mr: 1 }}
                checked={displayMacros}
                onChange={(e) => setDisplayMacros(e.target.checked)} />
              }
              label="Display Macros"
            />
            <MenuBuilderExportButton form={form} weekLabels={weekLabels} />
            <Button
              variant="outlined"
              onClick={handlePublishClick}
              startIcon={<PublishOutlinedIcon />}>
              Publish
            </Button>
            <LoadingButton
              onClick={handleSave}
              buttonText={"Save"}
              loading={loadingSavePlannedMeals}
              icon={<SaveOutlinedIcon />} />
            {isMenuBuilderMultiWeekCopyingEnabled && weeks.length > 1 && selectedWeekIndex > 0 && (
              <>
                <Button
                  onClick={e => {
                    e.stopPropagation();
                    setMoreAnchorEl(e.currentTarget);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.stopPropagation();
                      setMoreAnchorEl(event.currentTarget);
                    }
                  }}
                  role="button"
                  aria-label="More Actions"
                  tabIndex={0}
                  variant="outlined"
                  sx={{ px: .5 }}
                >
                  <MoreVertIcon />
                </Button>
                <Menu anchorEl={moreAnchorEl} open={Boolean(moreAnchorEl)} onClose={() => setMoreAnchorEl(null)} keepMounted>
                  {weeks.map((week, index) => {
                    // Only show weeks before the current selected week
                    if (index >= selectedWeekIndex) return null;

                    return (
                      <MenuItem
                        key={week.id}
                        disabled={!checkWeekHasData(index)}
                        onClick={() => {
                          handleCopyWeekClick(index);
                          setMoreAnchorEl(null);
                        }}
                      >
                        Copy from Week {index + 1}
                      </MenuItem>
                    );
                  })}
                </Menu>
              </>
            )}
          </Box>
        )}
      </Box>
      <MenuBuilderPanelToCellDnd enabled={isDragAndDropMenuItemsEnabled}>
        <Box
          ref={gridContentRef}
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: "auto",
            // Shrink the scrollport by the overlay panel width so covered columns are reachable
            width: isDragAndDropMenuItemsEnabled ? `calc(100% - ${menuItemsPanelWidth}px)` : "100%",
            transition: theme =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          }}
        >
          {weeks.map((week, index) => {
            return <MenuBuilderWeek
              key={week.id}
              weekFormIndex={index}
              weekId={week.id}
              form={form} />;
          })}
        </Box>
        {isDragAndDropMenuItemsEnabled && (
          <Box
            sx={{
              position: "fixed",
              top: panelTop,
              right: 0,
              bottom: 0,
              zIndex: theme => theme.zIndex.drawer,
            }}
          >
            <MenuItemsSidePanel
              isExpanded={isMenuItemsPanelExpanded}
              onToggleExpanded={() => setIsMenuItemsPanelExpanded(prev => !prev)}
            />
          </Box>
        )}
      </MenuBuilderPanelToCellDnd>

      <ConfirmationDialog
        open={weeksWithErrors.length > 0}
        title="Unable to Save Menu"
        message={
          <Box>
            <Typography>
              The menu cannot be saved due to unresolved issues. Please check and fix the errors on the following weeks in order to proceed:
            </Typography>
            <List sx={{ listStyleType: "disc", pl: 3, pb: 0 }}>
              {weeksWithErrors.map(weekIndex => (
                <ListItem sx={{ display: "list-item", p: 0 }}>{weekLabels[weekIndex]}</ListItem>
              ))}
            </List>
          </Box>
        }
        onCancel={() => setWeeksWithErrors([])}
        onConfirm={() => setWeeksWithErrors([])}
      />

      <UnsavedChangesDialog
        open={unsavedChangesDialogOpen}
        title="Unsaved Changes"
        message={`The menu has unsaved changes. Please make sure to save the menu in order to publish it.`}
        onCancel={() => {
          setUnsavedChangesDialogueOpen(false);
        }}
        onConfirm={() => {
          setUnsavedChangesDialogueOpen(false);
        }}
        messageSx={{ color: theme => theme.palette.common.black }}
      />

      <SomethingWentWrongDialog
        onClose={() => setErrorWhileSavingDialog(false)}
        open={errorWhileSavingDialog}
        loading={loading}
        content={"We were unable to save the menu due to internal server errors. Please try again by clicking on “Save”."}
      />

      <Dialog open={loadingSavePlannedMeals}>
        <DialogTitle>Save Menu</DialogTitle>
        <DialogContent>
          <Loading />
          <Typography sx={{ display: "flex", alignSelf: "center" }} variant="h4">
            Saving in progress...
          </Typography>
          <Typography variant="body2" color="mediumEmphasisText">
            Please sit back and wait momentarily as we finish saving the menu.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={lockTakenDetails !== null}>
        <DialogTitle>Lost Editing Access</DialogTitle>
        <DialogContent>
          <Typography>
            {lockTakenDetails?.lockCreatedByName} has taken over the access to edit the menu {lockTakenDetails?.lockCreatedAgo} ago. Please
            note that you will only be able to view the menu now.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLockTakenDetails(null);
            }}
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>

      <SomethingWentWrongDialog
        onClose={() => setErrorWhileLockingMenu(false)}
        open={errorWhileLockingMenu}
        content={
          "Looks like something went wrong. Please try closing and reopening the menu. If the issue persists, please contact Nutrition support."
        }
      />

      <ConfirmationDialog
        open={copyWeekConfirmation !== null}
        title="Replace Menu"
        message={
          copyWeekConfirmation
            ? `The current week already has menu items in place. Copying the menu from Week ${copyWeekConfirmation.sourceWeekIndex + 1} will replace all existing items. Are you sure you want to proceed?`
            : ""
        }
        messageSx={{ height: "128px" }}
        onCancel={() => setCopyWeekConfirmation(null)}
        onConfirm={handleConfirmCopyWeek}
        variant="containedDestructive"
        confirmLabel="Yes"
      />
    </Box>
  );
};
